/**
 * Browser-side preparation of uploads: a small JPEG thumbnail and the pixel
 * size for a photo, and a poster frame for a video file.
 *
 * Done here rather than on the server because the browser already decodes
 * every format it can display — and, unlike Java's ImageIO, it honours a
 * phone photo's EXIF orientation, so thumbnails come out the right way up.
 * Everything is best-effort: a format the browser cannot decode (HEIC outside
 * Safari, say) still uploads, just without a thumbnail.
 */

import { adminApi } from "../api/admin";

/** Longest edge of a generated thumbnail, in pixels — the size the library's existing thumbnails use. */
const THUMB_EDGE = 600;

export interface PreparedImage {
  width: number;
  height: number;
  thumbnail: Blob | null;
}

/** Decode an image file for its size and a scaled-down JPEG; null if the browser can't decode it. */
export async function prepareImage(file: File, maxEdge = THUMB_EDGE): Promise<PreparedImage | null> {
  if (typeof createImageBitmap !== "function") return null;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null;
  }
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const thumbnail = await drawToJpeg(bitmap, Math.round(width * scale), Math.round(height * scale));
    return { width, height, thumbnail };
  } finally {
    bitmap.close();
  }
}

/** Grab a frame about a second into a video file, as a JPEG; null if that fails. */
export async function captureVideoPoster(file: File, maxEdge = THUMB_EDGE): Promise<Blob | null> {
  const src = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.preload = "metadata";
  video.src = src;
  try {
    await waitFor(video, "loadeddata");
    video.currentTime = Math.min(1, (video.duration || 2) / 2);
    await waitFor(video, "seeked");
    const scale = Math.min(1, maxEdge / Math.max(video.videoWidth, video.videoHeight));
    return await drawToJpeg(video, Math.round(video.videoWidth * scale), Math.round(video.videoHeight * scale));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(src);
  }
}

/** Upload a photo, plus a generated thumbnail when one could be made. */
export async function uploadPhotoFile(file: File): Promise<{
  url: string;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
}> {
  const prepared = await prepareImage(file);
  const { url } = await adminApi.uploadFile(file);
  let thumbnailUrl: string | null = null;
  if (prepared?.thumbnail) {
    thumbnailUrl = (await adminApi.uploadFile(asJpeg(prepared.thumbnail, file.name))).url;
  }
  return { url, thumbnailUrl, width: prepared?.width ?? null, height: prepared?.height ?? null };
}

/** Upload a video file, plus a poster frame when one could be captured. */
export async function uploadVideoFile(file: File): Promise<{ url: string; thumbnailUrl: string | null }> {
  const poster = await captureVideoPoster(file);
  const { url } = await adminApi.uploadFile(file);
  const thumbnailUrl = poster ? (await adminApi.uploadFile(asJpeg(poster, file.name))).url : null;
  return { url, thumbnailUrl };
}

function asJpeg(blob: Blob, originalName: string): File {
  const stem = originalName.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${stem}-thumb.jpg`, { type: "image/jpeg" });
}

async function drawToJpeg(source: CanvasImageSource, width: number, height: number): Promise<Blob | null> {
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
}

/** Resolve on an element's event, reject on its error or after a timeout. */
function waitFor(el: HTMLMediaElement, event: string, ms = 8000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => done(new Error("timed out")), ms);
    const onEvent = () => done();
    const onError = () => done(new Error("media error"));
    function done(err?: Error) {
      window.clearTimeout(timer);
      el.removeEventListener(event, onEvent);
      el.removeEventListener("error", onError);
      if (err) reject(err);
      else resolve();
    }
    el.addEventListener(event, onEvent);
    el.addEventListener("error", onError);
  });
}
